import { Injectable } from '@angular/core';
import { Http } from '@angular/http';
import { Application } from 'app/shared/services/app/application/application.model';
import { UserApplicationProperties } from './user-application-properties.model';
import { Session } from 'app/shared/services/session/session.service';
import { CONFIG } from 'app/config/config';
import { GlobalUser } from './global-user.model';
import { AppUser } from './app-user.model';

@Injectable()
export class UserService {

  private _allGlobalUsers: GlobalUser[];
  private _allGlobalUsersPromise: Promise<GlobalUser[]>;
  private _globalUserById: GlobalUser;
  private _globalUserByIdPromise: Promise<GlobalUser>;
  private _appUsers: AppUser[];
  private _appUserById: AppUser;

  constructor(private _http: Http, private _session: Session) { }

  refreshUsers(): Promise<GlobalUser[]> {
    this._allGlobalUsers = null;
    return this.getAllGlobalUsers();
  }

  getAllGlobalUsers(): Promise<GlobalUser[]> {
    if (this._allGlobalUsers) {
      return Promise.resolve(this._allGlobalUsers);
    } else if (this._allGlobalUsersPromise) {
      return this._allGlobalUsersPromise;
    } else {
      this._allGlobalUsersPromise = this._session.user()
      .then((currentUser) => {
        return this._http.get(CONFIG.ENDPOINT_URL + 'users', { headers: this._session.headers() })
          .toPromise()
          .then((data) => {
            let jsonData = data.json();
            let users: GlobalUser[] = [];

            jsonData.forEach((jsonUser) => {
              let newUser = new GlobalUser(jsonUser);
              this.processGlobalUserApps(newUser, jsonUser.apps);
              users.push(newUser);
            });

            // There's probably a better way to do this too
            setTimeout(() => {
              this._allGlobalUsersPromise = null;
            }, 300);
            this._allGlobalUsers = users;
            return users;
          });
      });
      return this._allGlobalUsersPromise;
    }
  }

  getGlobalUserById(id: any): Promise<GlobalUser> {
    if (this._globalUserById && this._globalUserById.id == id) {
      return Promise.resolve(this._globalUserById);
    } else {
      this._globalUserByIdPromise =  this._http.get(CONFIG.ENDPOINT_URL + 'users/' + id, { headers: this._session.headers() })
        .toPromise()
        .then((data) => {
          let jsonData = data.json();
          let newUser = new GlobalUser(jsonData);
          this.processGlobalUserApps(newUser, jsonData.apps);
          this._globalUserById = newUser;
          return newUser;
        });
        return this._globalUserByIdPromise;
    }
  }

  processGlobalUserApps(user: GlobalUser, jsonApps: any[]) {
    user.roles = [];
    user.applications = [];
    jsonApps.forEach((jsonApp) => {
      let app = this._session.getAppFromMasterHash(jsonApp.app);
      let appRole = new UserApplicationProperties(jsonApp.role, app);
      user.applications.push(app);
      user.roles.push(appRole);
      user.appHash[jsonApp.app] = app;
    });
  }

  deleteGlobaUser(user: GlobalUser) {
    return this._http
      .delete(CONFIG.ENDPOINT_URL + 'users/' + user.id, { headers: this._session.headers() })
      .toPromise();
  }

  createGlobalUser(admin_obj: any) {
    return this._http
      .post(CONFIG.ENDPOINT_URL + 'auth/user', admin_obj, { headers: this._session.headers() })
      .toPromise()
      .then(re => {
        let jsonUser = re.json();
        let user = new GlobalUser(jsonUser);
        this.processGlobalUserApps(user, jsonUser.app_role_names);
        this._allGlobalUsers.push(user);
        return re.json();
      })
      .catch(e => { return e.json(); });
  }

  getAllAppUsers(appName: string, clearCache?: boolean): Promise<AppUser[]> {
    if (clearCache) this._appUsers = null;
    else if (this._appUsers) {
      return Promise.resolve(this._appUsers);
    }
    return this._http.get(CONFIG.ENDPOINT_URL + 'apps/' + appName + '/users?is_new_format=true', { headers: this._session.headers() })
      .toPromise()
      .then((data: any) => {
        let jsonData = data.json();
        let users: AppUser[] = [];

        jsonData.forEach((jsonUser) => {
          let newUser = new AppUser(jsonUser);
          // process roles/apps when final payload established
          users.push(newUser);
        });

        this._appUsers = users;
        return users;
      });
  }

  getAppUserById(appName: string, id: any) {
    return this._appUsers.filter(user => user.id == id)[0];
  }

  updateAppUserById(id: number, updates: any[]) {
    let selectedUser = this._appUsers.filter(user => user.id == id)[0];
    updates.forEach(item => {
      selectedUser[item.key] = item.value;
    });
  }

  getAppUserDetail(appName: string, id: any): Promise<AppUser> {
    if (this._appUserById && this._appUserById.id == id) {
      return Promise.resolve(this._appUserById);
    } else {
      return this._http.get(CONFIG.ENDPOINT_URL + 'apps/' + appName + '/users/' + id + '?is_new_format=true', { headers: this._session.headers() })
      .toPromise()
      .then((data) => {
        let jsonData = data.json();
        let newUser = new AppUser(jsonData);
        // process roles/apps when final payload established
        this._appUserById = newUser;
        return newUser;
      });
    }
  }

  checkIfUserExists(email: string) {
    return this._http.post(CONFIG.ENDPOINT_URL + 'users/exists', {email: email}, { headers: this._session.headers() })
      .toPromise()
      .then((response) => {
        return response.json();
      })
      .catch(error => { console.log(error); });
  }

  // The edit user object does not necessarily need to be a user object because it can take as few as one parameter.
  editUserProfile(id: any, editObj: any) {
    return this._http
      .put(CONFIG.ENDPOINT_URL + 'users/' + id, editObj, { headers: this._session.headers() })
      .toPromise()
      .then((response) => { return response.json(); });
  }

  // separate because it gets really unwieldly to model a nested body elsewhere, so let's just handle it here
  updateUserRemoteCache(id: any, editObj: any) {
    let body = {
      remote_cache: editObj
    };
    return this.editUserProfile(id, body);
  }

  editUserPermission(id: any, permissions: string[]) {
    return this._http
      .post(CONFIG.ENDPOINT_URL + 'users/' + id + '/permissions', { permissions: permissions }, { headers: this._session.headers() })
      .toPromise()
      .then(response => response.json());
  }

  reinviteUsers(userIds: number[]) {
    return this._http.post(CONFIG.ENDPOINT_URL + 'emails/reinvite', { users: userIds }, { headers: this._session.headers() })
    .toPromise()
    .catch((e) => {
      console.log(e);
      return e;
    });
  }

  passwordResetEmail(email: string) {
    return this._http
      .post(CONFIG.ENDPOINT_URL + 'passwords', { email: email }, { headers: this._session.headers() })
      .toPromise();
  }

  passwordResetEmailMobile(email: string, app: string) {
    return this._http
      .post(CONFIG.ENDPOINT_URL + 'passwords', { email: email, app: app }, { headers: this._session.headers() })
      .toPromise();
  }

  confirmEmail(token: string, userObj: any) {
    return this._http
      .post(CONFIG.ENDPOINT_URL + 'confirm_email/' + token,
            { password: userObj.password, first_name: userObj.first_name, last_name: userObj.last_name, company: userObj.company})
      .toPromise()
      .then(response => { return response.json(); });
  }

  // expects an array of objects with property email: string and features: string[]
  addUsersToApp(appId: string, user: any) {
    let body = {
      users: user
    };
    return this._http.post(CONFIG.ENDPOINT_URL + 'apps/' + appId + '/users/invite', body, { headers: this._session.headers() })
    .toPromise()
    .then((response) => { return response.json(); })
    .catch((error) => { console.log(error.json()); });
  }

  // updates a group of users to share information
  // DO NOT use for updating app roles or permissions
  batchEditUserProfiles(editObj: any) {
    return this._http
      .put(CONFIG.ENDPOINT_URL + 'users/', editObj, { headers: this._session.headers() })
      .toPromise()
      .then((response) => { return response; });
  }

  deleteUsersFromApp(users: AppUser[], appId: string) {
    let userString = '';
    users.forEach((user) => {
      userString += 'users[]=' + user.id + '&';
    });
    userString = userString.substring(0, userString.length - 1); // removes last ampersand
    return this._http
      .delete(CONFIG.ENDPOINT_URL + 'apps/' + appId + '/users?' + userString, { headers: this._session.headers() })
      .toPromise()
      .then((response) => {
        return response.json();
      })
      .catch(error => { console.log(error); });
  }

  updateUserPermissions(appId: string, id: any, features: any[]) {
    let body = { permissions: features };
    return this._http.post(CONFIG.ENDPOINT_URL + 'apps/' + appId + '/users/' + id + '/permissions',
           body,
           { headers: this._session.headers() })
      .toPromise()
      .then((response) => { return response.json(); })
      .catch((error) => { console.log(error.json()); });
  }

   importUsers(app: Application, userCsv: File, indexes: any[], hasHeaders: boolean, hasEmail: boolean) {
     let formData = new FormData();
     formData.append('users_file', userCsv);
     formData.append('use_email', hasEmail.toString());
     formData.append('user_indexes', indexes.toString());
     formData.append('headers', hasHeaders.toString());
     return this._http.post(CONFIG.ENDPOINT_URL + 'apps/' + app.sys_name + '/users/import', formData, { headers: this._session.headers() })
      .toPromise()
      .then((response) => { return response; })
      .catch((error) => { console.log(error.json()); });
   }
 }



// WEBPACK FOOTER //
// /app/src/app/shared/services/user/user.service.ts